import axios from 'axios';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const API = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

const api = axios.create({
  baseURL: API,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

// ── Request interceptor ─────────────────────────────────────────────
api.interceptors.request.use((config) => {
  if (config.data instanceof FormData) {
    if (typeof config.headers?.delete === 'function') {
      config.headers.delete('Content-Type');
    } else if (config.headers) {
      delete config.headers['Content-Type'];
      delete config.headers['content-type'];
    }
  }
  // Rely on httpOnly cookies; do NOT inject Bearer token from localStorage
  return config;
});

// ── Token refresh queue ──────────────────────────────────────────────
let isRefreshing = false;
let refreshQueue = [];

function processQueue(error, token = null) {
  refreshQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  refreshQueue = [];
}

// ── Response interceptor: auto-refresh on 401 ────────────────────────
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Only attempt refresh on 401, and only once per request
    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    // If a refresh is already in flight, queue this request
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        refreshQueue.push({ resolve, reject });
      }).then(() => {
        // Cookies are sent automatically with withCredentials
        return api(originalRequest);
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
      // Replay queued requests
      processQueue(null, true);
      // Retry the original request — cookies handle auth automatically
      return api(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError, null);
      // Redirect to login (avoid redirect loops on login page itself)
      if (!window.location.pathname.startsWith('/login')) {
        window.location.href = '/login';
      }
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

export default api;
export { API, BACKEND_URL };
